import React, { useState, useEffect, useCallback } from "react";
import { Wallet, CalendarDays } from "lucide-react";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import { myBalances, listLeaveTypesDropdown, listHolidays } from "../../../services/hrService";
import { LeaveBalance, LeaveType, Holiday } from "../../../services/hrTypes";

const isWeeklyOff = (kind?: string) => (kind || "").toUpperCase() === "WEEKLY_OFF";

const MyLeavePeriod: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [balances, setBalances] = useState<LeaveBalance[]>([]);
    const [types, setTypes] = useState<Pick<LeaveType, "id" | "name" | "code">[]>([]);
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [b, t, h] = await Promise.all([myBalances(), listLeaveTypesDropdown(), listHolidays()]);
            setBalances(b);
            setTypes(t);
            setHolidays(h);
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const typeName = (id: number) => types.find((t) => t.id === id)?.name || `Type #${id}`;

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>My Leave Period</span>
            </div>

            <div className="netbody-content" style={{ overflowY: "auto" }}>
                {/* Balances as cards */}
                <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: 10 }}>My Balances</div>
                {loading && (
                    <div className="action-card-grid action-card-grid--compact">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="action-card action-card--skeleton" data-testid={`CARD_balance_skel_${i}`}>
                                <div className="action-card__skel-header">
                                    <Skeleton.Circle size={36} />
                                    <Skeleton.Box width="60%" height={14} />
                                </div>
                                <Skeleton.Box width="40%" height={20} />
                            </div>
                        ))}
                    </div>
                )}
                {!loading && balances.length === 0 && (
                    <NoDataFound title="No balances" message="No leave balances have been allotted yet." />
                )}
                {!loading && balances.length > 0 && (
                    <div className="action-card-grid action-card-grid--compact">
                        {balances.map((b) => {
                            const available = b.allotted - b.used - b.pending;
                            return (
                                <div key={b.id} className="action-card" data-testid={`CARD_balance_${b.id}`}>
                                    <div className="action-card__header">
                                        <span className="action-card__icon"><Wallet size={18} /></span>
                                        <div className="action-card__title-block">
                                            <div className="action-card__title">{typeName(b.leave_type_id)}</div>
                                            <div className="action-card__subtitle">Year {b.year}</div>
                                        </div>
                                        <span className="action-card__badge">{available} left</span>
                                    </div>
                                    <div className="action-card__meta action-card__meta--piped">
                                        <span className="action-card__meta-item">Allotted {b.allotted}</span>
                                        <span className="action-card__meta-item">Used {b.used}</span>
                                        <span className="action-card__meta-item">Pending {b.pending}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Holidays */}
                <div style={{ fontWeight: 600, fontSize: "0.875rem", margin: "1.25rem 0 10px" }}>Holidays</div>
                <div className="table-list">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Date</th>
                                <th className="table-list-th">Holiday</th>
                                <th className="table-list-th">Kind</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={["3xs", "sm", "md", "pill"]} />}
                            {!loading && holidays.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="table-list-cell">
                                        <NoDataFound title="No holidays" message="No holidays published for this period." />
                                    </td>
                                </tr>
                            )}
                            {!loading && holidays.map((h, idx) => (
                                <tr key={h.id} className="table-list-row" data-testid={`ROW_holiday_${h.id}`}>
                                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                    <td className="table-list-cell"><CalendarDays size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />{formatDate(h.holiday_date)}</td>
                                    <td className="table-list-cell"><span className="table-list-cell-title">{h.name}</span></td>
                                    <td className="table-list-cell">
                                        <span className={`status-badge xs ${isWeeklyOff(h.holiday_kind) ? "neutral" : "info"}`}>
                                            {isWeeklyOff(h.holiday_kind) ? "Weekly off" : h.holiday_kind}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default MyLeavePeriod;
